import React from "react";

export interface PolicyLayoutProps {
  title: string;
  intro?: string;
  updated?: string;
  children: React.ReactNode;
}

export interface PolicySectionProps {
  title: string;
  children: React.ReactNode;
}

export function PolicySection({ title, children }: PolicySectionProps) {
  return (
    <section className="space-y-3 pt-6 border-t border-border first:pt-0 first:border-t-0">
      <h2 className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-foreground">
        {title}
      </h2>
      <div className="space-y-4 text-muted-foreground leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function PolicyLayout({
  title,
  intro,
  updated,
  children,
}: PolicyLayoutProps) {
  return (
    <article
      data-nav="light"
      className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border"
    >
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header Block */}
          <header className="pb-8 mb-10 border-b border-border">
            {/* Signature Brand Accent */}
            <div className="flex items-center gap-2 mb-4" aria-hidden="true">
              <span className="w-8 h-[2px] bg-secondary" />
              <span className="w-3 h-[2px] bg-primary" />
              <span className="text-[11px] font-mono tracking-wider uppercase text-muted-foreground font-semibold">
                AquaFlame Legal &amp; Policies
              </span>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-[2.65rem] font-bold tracking-tight text-foreground leading-[1.15]">
              {title}
            </h1>

            {intro && (
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                {intro}
              </p>
            )}

            {updated && (
              <p className="mt-4 text-xs font-mono tracking-wide text-muted-foreground uppercase">
                Last updated: {updated}
              </p>
            )}
          </header>

          {/* Readable Prose Content Body */}
          <div className="space-y-8 [&_h2]:font-serif [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-foreground [&_h2]:pt-6 [&_h2]:border-t [&_h2]:border-border [&_h2:first-child]:pt-0 [&_h2:first-child]:border-t-0 [&_h3]:font-serif [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:text-base [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:text-muted-foreground [&_li]:leading-relaxed [&_a]:text-primary [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80">
            {children}
          </div>
        </div>
      </div>
    </article>
  );
}

export { PolicyLayout };
